import type { ElementCategory, ElementRef } from "./properties.ts";
import type { Point, SwmmProject } from "./types.ts";
import { polygonCentroid } from "./bounds.ts";
import { getCaseInsensitive } from "./idLookup.ts";

/** `kind` is never part of an `ElementRef`'s identity — it's a rendering/filtering hint only, never compared. */
export function sameElementRef(a: ElementRef, b: ElementRef): boolean {
  return a.category === b.category && a.name === b.name;
}

export function mergeSelection(
  current: ElementRef[],
  next: ElementRef[],
  combine: boolean
): ElementRef[] {
  if (!combine) return next;
  const merged = [...current];
  for (const ref of next) {
    if (!merged.some((r) => sameElementRef(r, ref))) merged.push(ref);
  }
  return merged;
}

export type AreaDrag =
  { shape: "rect"; start: Point; current: Point } | { shape: "lasso"; points: Point[] };

export function areaDragPolygon(drag: AreaDrag): Point[] {
  if (drag.shape === "lasso") return drag.points;
  return [
    drag.start,
    { x: drag.current.x, y: drag.start.y },
    drag.current,
    { x: drag.start.x, y: drag.current.y },
  ];
}

export function representativePoint(project: SwmmProject, ref: ElementRef): Point | null {
  // getCaseInsensitive: a [COORDINATES] row's own name can legally differ in
  // case from the node section row it belongs to (real ids are matched
  // case-insensitively) — a plain `.get` would leave the point dangling.
  if (ref.category === "node") return getCaseInsensitive(project.coordinates, ref.name) ?? null;
  if (ref.category === "raingage") return getCaseInsensitive(project.symbols, ref.name) ?? null;
  if (ref.category === "link") {
    const link = project.links.find((l) => l.name === ref.name);
    if (!link) return null;
    const from = getCaseInsensitive(project.coordinates, link.fromNode);
    const to = getCaseInsensitive(project.coordinates, link.toNode);
    return from && to ? { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 } : null;
  }
  const polygon = project.polygons.get(ref.name);
  return polygon ? polygonCentroid(polygon) : null;
}

/** `polygon` doesn't need to be explicitly closed — the last point implicitly connects back to the first. */
export function pointInPolygon(point: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const pi = polygon[i];
    const pj = polygon[j];
    const intersects =
      pi.y > point.y !== pj.y > point.y &&
      point.x < ((pj.x - pi.x) * (point.y - pi.y)) / (pj.y - pi.y) + pi.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

export function selectWithinPolygon(project: SwmmProject, polygon: Point[]): ElementRef[] {
  if (polygon.length < 3) return [];
  const refs: ElementRef[] = [
    ...project.nodes.map((n) => ({ category: "node" as const, kind: n.kind, name: n.name })),
    ...project.links.map((l) => ({ category: "link" as const, kind: l.kind, name: l.name })),
    ...project.subcatchments.map((s) => ({
      category: "subcatchment" as const,
      kind: "subcatchment" as const,
      name: s.name,
    })),
    ...project.raingages.map((r) => ({
      category: "raingage" as const,
      kind: "raingage" as const,
      name: r.name,
    })),
  ];
  return refs.filter((ref) => {
    const point = representativePoint(project, ref);
    return point !== null && pointInPolygon(point, polygon);
  });
}

export function selectByKind(
  project: SwmmProject,
  category: ElementCategory,
  kind: string
): ElementRef[] {
  if (category === "node") {
    return project.nodes
      .filter((n) => n.kind === kind)
      .map((n) => ({ category, kind: n.kind, name: n.name }));
  }
  if (category === "link") {
    return project.links
      .filter((l) => l.kind === kind)
      .map((l) => ({ category, kind: l.kind, name: l.name }));
  }
  if (category === "subcatchment") {
    return project.subcatchments.map((s) => ({
      category,
      kind: "subcatchment" as const,
      name: s.name,
    }));
  }
  return project.raingages.map((r) => ({ category, kind: "raingage" as const, name: r.name }));
}
